/**
 * W66.G — OrgMapEmbed
 * Embeds the Organizational Mapping surface inline within the account scorecard tab.
 * Full CRUD — same features as the standalone /account/:id/org-map page.
 *
 * Advisory only — does not affect M-PACT composite score.
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "wouter";
import { Plus, ExternalLink, Users } from "lucide-react";
import { api, getSession } from "@/lib/session";
import type { OrgNode } from "./types";
import OrgNodeCard from "./NodeCard";
import AddNodeModal from "./AddNodeModal";
import { rolePillClass } from "./types";

interface Props {
  accountId: number;
}

interface AccountMeta {
  id: number;
  name: string;
  industry: string | null;
  owner_user_id: string;
  ownerId?: string;
}

export default function OrgMapEmbed({ accountId }: Props) {
  const [account, setAccount] = useState<AccountMeta | null>(null);
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const session = getSession();
  const userRole = session?.role ?? "";
  const userId = session?.id ?? "";

  function canEdit(): boolean {
    if (!account || !session) return false;
    if (userRole === "manager" || userRole === "exec" || userRole === "executive") return true;
    return (account.owner_user_id ?? account.ownerId) === userId;
  }

  const fetchNodes = useCallback(async () => {
    try {
      const data = await api<OrgNode[]>(`/api/account-org-map/${accountId}/nodes`);
      setNodes(data ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load org map");
    }
  }, [accountId]);

  const fetchAccount = useCallback(async () => {
    try {
      const data = await api<AccountMeta>(`/api/accounts/${accountId}`);
      setAccount(data);
    } catch {
      // non-fatal; template button just won't appear
    }
  }, [accountId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchAccount(), fetchNodes()]).finally(() => setLoading(false));
  }, [fetchAccount, fetchNodes]);

  async function handleAddNode(data: Partial<OrgNode>) {
    await api(`/api/account-org-map/${accountId}/nodes`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    await fetchNodes();
    setNotice({ kind: "success", text: "Person added." });
  }

  async function handleSaveNode(id: number, patch: Partial<OrgNode>) {
    await api(`/api/account-org-map/nodes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    await fetchNodes();
    setNotice({ kind: "success", text: "Updated." });
  }

  async function handleDeleteNode(id: number) {
    try {
      await api(`/api/account-org-map/nodes/${id}`, { method: "DELETE" });
      await fetchNodes();
      setNotice({ kind: "success", text: "Person removed." });
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message ?? "Delete failed" });
    }
  }

  async function handleApplyTemplate() {
    const industry = account?.industry ?? "";
    if (!["Automotive", "Manufacturing"].includes(industry)) return;
    if (!window.confirm(`Apply the ${industry} buying committee template? This will pre-populate 7 roles.`)) return;
    setApplyingTemplate(true);
    try {
      const result = await api<{ inserted: number }>(`/api/account-org-map/${accountId}/apply-template`, {
        method: "POST",
        body: JSON.stringify({ industry }),
      });
      await fetchNodes();
      setNotice({ kind: "success", text: `${result.inserted} roles pre-populated from the ${industry} template.` });
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message ?? "Template apply failed" });
    } finally {
      setApplyingTemplate(false);
    }
  }

  function buildTree(nodes: OrgNode[]): { roots: OrgNode[]; childMap: Map<number, OrgNode[]> } {
    const childMap = new Map<number, OrgNode[]>();
    const roots: OrgNode[] = [];
    for (const n of nodes) {
      if (n.reports_to_node_id) {
        const siblings = childMap.get(n.reports_to_node_id) ?? [];
        siblings.push(n);
        childMap.set(n.reports_to_node_id, siblings);
      } else {
        roots.push(n);
      }
    }
    return { roots, childMap };
  }

  function renderTree(
    nodeList: OrgNode[],
    childMap: Map<number, OrgNode[]>,
    depth = 0,
  ): React.ReactNode {
    return nodeList.map((node) => {
      const children = childMap.get(node.id) ?? [];
      return (
        <div key={node.id} style={{ marginLeft: depth * 20 }} className="mt-3">
          <OrgNodeCard
            node={node}
            canEdit={canEdit()}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
          />
          {children.length > 0 && renderTree(children, childMap, depth + 1)}
        </div>
      );
    });
  }

  const { roots, childMap } = buildTree(nodes);
  const industry = account?.industry ?? "";
  const showTemplateBtn =
    canEdit() &&
    ["Automotive", "Manufacturing"].includes(industry) &&
    nodes.length === 0;

  return (
    <section data-testid="org-map-embed">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-[16px] font-semibold tracking-tight">Organizational Mapping</h2>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Advisory only — does not affect Composite score. Use this to keep the buying committee organized.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showTemplateBtn && (
            <button
              type="button"
              onClick={handleApplyTemplate}
              disabled={applyingTemplate}
              className="text-[11px] px-2.5 py-1 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition disabled:opacity-50"
              data-testid="embed-apply-template-btn"
            >
              {applyingTemplate ? "Applying..." : `Apply ${industry} template`}
            </button>
          )}
          {canEdit() && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border bg-foreground text-background border-foreground hover:opacity-90 transition"
              data-testid="embed-add-person-btn"
            >
              <Plus className="w-3 h-3" /> Add person
            </button>
          )}
          <Link
            href={`/account/${accountId}/org-map`}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition text-muted-foreground"
            title="Open full Org Map page"
          >
            <ExternalLink className="w-3 h-3" /> Full view
          </Link>
        </div>
      </div>

      {/* Notice */}
      {notice && (
        <div
          role="status"
          className={`mb-3 px-3 py-2 rounded text-[12px] border ${
            notice.kind === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-800"
          }`}
        >
          {notice.text}
          <button type="button" onClick={() => setNotice(null)} className="ml-3 underline opacity-70 hover:opacity-100">
            Dismiss
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-800 text-[12px] mb-3">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-[12px] text-muted-foreground py-4">Loading...</div>
      ) : nodes.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
          <Users className="w-6 h-6 mx-auto mb-2 opacity-30" />
          <p className="mb-1">No people mapped yet.</p>
          <p className="text-[11px]">
            {showTemplateBtn
              ? `Use "Apply ${industry} template" to pre-populate roles, or add people individually.`
              : "Add people using the button above."}
          </p>
        </div>
      ) : (
        <div data-testid="org-map-tree">
          {/* Legend pills */}
          <div className="mb-3 flex flex-wrap gap-1.5 text-[10px]">
            {(["Decision Maker","Economic Buyer","Champion","Influencer","User","Blocker","Unknown"] as const).map((r) => (
              <span key={r} className={`inline-block px-2 py-0.5 rounded-full ${rolePillClass(r)}`}>{r}</span>
            ))}
          </div>
          {renderTree(roots, childMap)}
          <div className="mt-4 text-[11px] text-muted-foreground text-right">
            {nodes.length} {nodes.length === 1 ? "person" : "people"} mapped
          </div>
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <AddNodeModal
          accountId={accountId}
          nodes={nodes}
          onSave={handleAddNode}
          onClose={() => setShowAdd(false)}
        />
      )}
    </section>
  );
}
